
import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export type Role = 'admin' | 'moderator' | 'user';

// Role cache to prevent unnecessary API calls
const roleCache = new Map<string, { role: Role, timestamp: number }>();
const CACHE_EXPIRY = 5 * 60 * 1000; // Cache roles for 5 minutes

export const useRole = (userId?: string | null) => {
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUserRole = useCallback(async (uid: string) => {
    // Check cache first
    const cachedData = roleCache.get(uid);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_EXPIRY) {
      console.log("Using cached role for user:", uid, cachedData.role);
      return cachedData.role;
    }

    try {
      console.log("Fetching role for user:", uid);
      
      // Fetch from user_roles table (secure approach)
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', uid)
        .order('role', { ascending: true }) // Get highest priority role (admin < moderator < user alphabetically)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Role fetch error:", error);
        throw error;
      }
      
      // Determine role based on database value
      let newRole: Role = 'user';
      if (data?.role === 'admin') {
        newRole = 'admin';
      } else if (data?.role === 'moderator') {
        newRole = 'moderator';
      }
      
      console.log("Fetched role for user:", uid, "Role:", newRole);
      
      // Cache the role
      roleCache.set(uid, { role: newRole, timestamp: Date.now() });
      
      return newRole;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return 'user' as Role; // Default to user instead of null
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const getRoleAsync = async () => {
      if (!userId) {
        if (isMounted) {
          setRole(null);
          setIsLoading(false);
        }
        return;
      }

      try {
        const userRole = await fetchUserRole(userId);
        
        if (isMounted) {
          setRole(userRole);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in useRole hook:', error);
        if (isMounted) {
          setRole(null);
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    getRoleAsync();

    return () => {
      isMounted = false;
    };
  }, [userId, fetchUserRole]);

  // Method to force refresh role
  const refreshRole = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      // Remove from cache to force a fresh fetch
      roleCache.delete(userId);
      const userRole = await fetchUserRole(userId);
      setRole(userRole);
    } catch (error) {
      console.error('Error refreshing role:', error);
      setRole('user' as Role); // Default to user instead of null
    } finally {
      setIsLoading(false);
    }
  }, [userId, fetchUserRole]);

  return { role, isLoading, refreshRole };
};

// Clear role cache function for debugging
export const clearRoleCache = () => {
  roleCache.clear();
  console.log("Role cache cleared");
};

// Helper to preload a user's role using direct database query
export const preloadUserRole = async (userId: string) => {
  if (!userId) return;
  
  const cachedData = roleCache.get(userId);
  if (cachedData && Date.now() - cachedData.timestamp < CACHE_EXPIRY) {
    return cachedData.role;
  }
  
  try {
    // Fetch from user_roles table
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .order('role', { ascending: true })
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.error('Error preloading user role:', error);
      return 'user' as Role;
    }
    
    // Determine role based on database value
    let role: Role = 'user';
    if (data?.role === 'admin') {
      role = 'admin';
    } else if (data?.role === 'moderator') {
      role = 'moderator';
    }
    
    roleCache.set(userId, { role, timestamp: Date.now() });
    return role;
  } catch (error) {
    console.error('Error preloading user role:', error);
    return 'user' as Role;
  }
};
